import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Typography,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { clientesList, productosList, conceptosVentaLibre } from './posData';

const NewSaleDialog = ({ open, onClose, onSave, tipoVenta = 'libre' }) => {
  const [tabValue, setTabValue] = useState(tipoVenta === 'productos' ? 1 : 0);
  
  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Datos para venta libre
  const [ventaLibre, setVentaLibre] = useState({
    fechaHora: new Date(),
    cliente: null,
    concepto: '',
    cantidad: 1,
    precioUnitario: '',
    descuento: 0,
    medioPago: 'efectivo',
  });

  // Datos para venta de productos
  const [ventaProductos, setVentaProductos] = useState({
    fechaHora: new Date(),
    cliente: null,
    productos: [],
    descuentoTotal: 0,
    medioPago: 'efectivo',
  });

  const [errors, setErrors] = useState({});

  const medioPagoOptions = [
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta_debito', label: 'Tarjeta Débito' },
    { value: 'tarjeta_credito', label: 'Tarjeta Crédito' },
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'cheque', label: 'Cheque' },
  ];

  // Calcular totales para venta libre
  const calcularTotalVentaLibre = () => {
    const subtotal = (ventaLibre.cantidad || 0) * (ventaLibre.precioUnitario || 0);
    const descuento = subtotal * ((ventaLibre.descuento || 0) / 100);
    const iva = (subtotal - descuento) * 0.13;
    return {
      subtotal,
      descuento,
      iva,
      total: subtotal - descuento + iva,
    };
  };

  // Calcular totales para venta de productos
  const calcularTotalesVentaProductos = () => {
    let subtotal = 0;
    ventaProductos.productos.forEach((p) => {
      const pSubtotal = (p.cantidad || 0) * (p.precio || 0);
      const pDescuento = pSubtotal * ((p.descuento || 0) / 100);
      subtotal += pSubtotal - pDescuento;
    });
    
    const descuentoTotal = subtotal * ((ventaProductos.descuentoTotal || 0) / 100);
    const iva = (subtotal - descuentoTotal) * 0.13;
    return {
      subtotal,
      descuentoTotal,
      iva,
      total: subtotal - descuentoTotal + iva,
    };
  };

  // Handlers para venta libre
  const handleVentaLibreChange = (field) => (event) => {
    setVentaLibre({
      ...ventaLibre,
      [field]: event.target.value,
    });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleVentaLibreDateChange = (newDate) => {
    setVentaLibre({ ...ventaLibre, fechaHora: newDate });
  };

  const handleClienteLibreChange = (event, newValue) => {
    setVentaLibre({ ...ventaLibre, cliente: newValue });
  };

  const handleConceptoLibreChange = (event, newValue) => {
    setVentaLibre({ ...ventaLibre, concepto: newValue || '' });
    if (errors.concepto) {
      setErrors({ ...errors, concepto: null });
    }
  };

  // Handlers para venta de productos
  const handleVentaProductosDateChange = (newDate) => {
    setVentaProductos({ ...ventaProductos, fechaHora: newDate });
  };

  const handleClienteProductosChange = (event, newValue) => {
    setVentaProductos({ ...ventaProductos, cliente: newValue });
  };

  const handleDescuentoTotalChange = (event) => {
    setVentaProductos({ ...ventaProductos, descuentoTotal: event.target.value });
  };

  const handleMedioPagoProductosChange = (event) => {
    setVentaProductos({ ...ventaProductos, medioPago: event.target.value });
  };

  const agregarProducto = () => {
    setVentaProductos({
      ...ventaProductos,
      productos: [
        ...ventaProductos.productos,
        {
          id: Date.now(),
          producto: null,
          cantidad: 1,
          precio: '',
          descuento: 0,
        },
      ],
    });
  };

  const eliminarProducto = (index) => {
    const nuevosProductos = [...ventaProductos.productos];
    nuevosProductos.splice(index, 1);
    setVentaProductos({ ...ventaProductos, productos: nuevosProductos });
  };

  const handleProductoChange = (index, field, value) => {
    const nuevosProductos = [...ventaProductos.productos];
    nuevosProductos[index][field] = value;
    
    // Si selecciona un producto, auto-llenar el precio
    if (field === 'producto' && value) {
      nuevosProductos[index].precio = value.precio;
    }
    
    setVentaProductos({ ...ventaProductos, productos: nuevosProductos });
  };

  const calcularSubtotalProducto = (producto) => {
    const subtotal = (producto.cantidad || 0) * (producto.precio || 0);
    const descuento = subtotal * ((producto.descuento || 0) / 100);
    return subtotal - descuento;
  };

  const validateVentaLibre = () => {
    const newErrors = {};
    if (!ventaLibre.concepto.trim()) newErrors.concepto = 'El concepto es requerido';
    if (!ventaLibre.precioUnitario || parseFloat(ventaLibre.precioUnitario) <= 0) {
      newErrors.precioUnitario = 'El precio debe ser mayor a 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateVentaProductos = () => {
    const newErrors = {};
    if (ventaProductos.productos.length === 0) {
      newErrors.productos = 'Debe agregar al menos un producto';
    }
    const hasErrors = ventaProductos.productos.some((p, index) => {
      if (!p.producto) {
        newErrors[`producto_${index}`] = 'Seleccione un producto';
        return true;
      }
      if (!p.cantidad || p.cantidad <= 0) {
        newErrors[`cantidad_${index}`] = 'Cantidad inválida';
        return true;
      }
      return false;
    });
    setErrors(newErrors);
    return !hasErrors && Object.keys(newErrors).length === 0;
  };

  const handleSaveVentaLibre = () => {
    if (validateVentaLibre()) {
      const totales = calcularTotalVentaLibre();
      const ventaData = {
        id: `VN${Date.now()}`,
        fecha: ventaLibre.fechaHora.toISOString().split('T')[0],
        hora: ventaLibre.fechaHora.toTimeString().slice(0, 5),
        tipo: 'venta',
        tipoVenta: 'libre',
        concepto: ventaLibre.concepto,
        valor: totales.total,
        cantidad: ventaLibre.cantidad,
        precioUnitario: ventaLibre.precioUnitario,
        descuento: ventaLibre.descuento,
        subtotal: totales.subtotal,
        iva: totales.iva,
        medioPago: medioPagoOptions.find(mp => mp.value === ventaLibre.medioPago)?.label || ventaLibre.medioPago,
        cliente: ventaLibre.cliente?.nombre || 'Consumidor Final',
        proveedor: null,
        empleado: 'Usuario Actual',
        estado: 'completada',
      };
      onSave(ventaData);
      handleReset();
      onClose();
    }
  };

  const handleSaveVentaProductos = () => {
    if (validateVentaProductos()) {
      const totales = calcularTotalesVentaProductos();
      const ventaData = {
        id: `VN${Date.now()}`,
        fecha: ventaProductos.fechaHora.toISOString().split('T')[0],
        hora: ventaProductos.fechaHora.toTimeString().slice(0, 5),
        tipo: 'venta',
        tipoVenta: 'productos',
        concepto: 'Venta de productos',
        valor: totales.total,
        productos: ventaProductos.productos.map(p => ({
          producto: p.producto.nombre,
          cantidad: p.cantidad,
          precio: p.precio,
          descuento: p.descuento,
          subtotal: calcularSubtotalProducto(p),
        })),
        descuentoTotal: ventaProductos.descuentoTotal,
        subtotal: totales.subtotal,
        iva: totales.iva,
        medioPago: medioPagoOptions.find(mp => mp.value === ventaProductos.medioPago)?.label || ventaProductos.medioPago,
        cliente: ventaProductos.cliente?.nombre || 'Consumidor Final',
        proveedor: null,
        empleado: 'Usuario Actual',
        estado: 'completada',
      };
      onSave(ventaData);
      handleReset();
      onClose();
    }
  };

  const handleReset = () => {
    setVentaLibre({
      fechaHora: new Date(),
      cliente: null,
      concepto: '',
      cantidad: 1,
      precioUnitario: '',
      descuento: 0,
      medioPago: 'efectivo',
    });
    setVentaProductos({
      fechaHora: new Date(),
      cliente: null,
      productos: [],
      descuentoTotal: 0,
      medioPago: 'efectivo',
    });
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const totalesLibre = calcularTotalVentaLibre();
  const totalesProductos = calcularTotalesVentaProductos();

  // Estilos para full-screen en móvil
  const getPaperStyles = () => {
    if (isMobile) {
      return {
        borderRadius: 0,
        height: '100%',
        maxHeight: '100%',
        width: '100%',
        maxWidth: '100%',
        m: 0,
      };
    }
    return {
      borderRadius: 2,
      maxHeight: '90vh',
    };
  };

  // Botones de acción grandes para touch
  const actionButtonStyles = isMobile ? {
    minHeight: 56,
    fontSize: '1rem',
    py: 1.5,
  } : {};

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: getPaperStyles(),
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
          // Header más alto en móvil para touch
          minHeight: isMobile ? 64 : 'auto',
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Nueva Venta
          </Typography>
          <IconButton 
            onClick={handleClose} 
            size={isMobile ? "medium" : "small"}
            sx={{ 
              // Touch target grande
              minWidth: 48, 
              minHeight: 48,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant={isMobile ? "fullWidth" : "standard"}
          >
            <Tab label={isMobile ? "Libre" : "Venta Libre"} />
            <Tab label={isMobile ? "Productos" : "Venta de Productos"} />
          </Tabs>
        </Box>

        <DialogContent 
          sx={{ 
            pt: 3,
            // Scroll independiente
            overflow: 'auto',
            // En móvil, ocupar todo el espacio disponible
            ...(isMobile && {
              height: 'calc(100% - 140px)', // Restar header y footer
            }),
          }}
        >
          {tabValue === 0 ? (
            // Venta Libre
            <Grid container spacing={isMobile ? 2 : 2}>
              {/* Fecha/Hora */}
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Fecha y Hora"
                  value={ventaLibre.fechaHora}
                  onChange={handleVentaLibreDateChange}
                  slotProps={{
                    textField: { 
                      fullWidth: true, 
                      size: isMobile ? 'medium' : 'medium' 
                    },
                  }}
                />
              </Grid>

              {/* Cliente */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={clientesList}
                  getOptionLabel={(option) => option.nombre || ''}
                  value={ventaLibre.cliente}
                  onChange={handleClienteLibreChange}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Cliente" 
                      size={isMobile ? 'medium' : 'medium'} 
                      placeholder="Seleccionar cliente" 
                    />
                  )}
                />
              </Grid>

              {/* Concepto */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={conceptosVentaLibre}
                  value={ventaLibre.concepto}
                  onChange={handleConceptoLibreChange}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Concepto"
                      required
                      error={!!errors.concepto}
                      helperText={errors.concepto}
                      size={isMobile ? 'medium' : 'medium'}
                    />
                  )}
                />
              </Grid>

              {/* Cantidad */}
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Cantidad"
                  type="number"
                  fullWidth
                  size={isMobile ? 'medium' : 'medium'}
                  value={ventaLibre.cantidad}
                  onChange={handleVentaLibreChange('cantidad')}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              {/* Precio Unitario */}
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Precio Unitario"
                  type="number"
                  fullWidth
                  required
                  size={isMobile ? 'medium' : 'medium'}
                  value={ventaLibre.precioUnitario}
                  onChange={handleVentaLibreChange('precioUnitario')}
                  error={!!errors.precioUnitario}
                  helperText={errors.precioUnitario}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Descuento */}
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Descuento %"
                  type="number"
                  fullWidth
                  size={isMobile ? 'medium' : 'medium'}
                  value={ventaLibre.descuento}
                  onChange={handleVentaLibreChange('descuento')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>

              {/* Medio de Pago */}
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  label="Medio de Pago"
                  fullWidth
                  size={isMobile ? 'medium' : 'medium'}
                  value={ventaLibre.medioPago}
                  onChange={handleVentaLibreChange('medioPago')}
                >
                  {medioPagoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Totales */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                      <Typography variant="h6">${totalesLibre.subtotal.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Descuento ({ventaLibre.descuento || 0}%)</Typography>
                      <Typography variant="h6" color="error.main">-${totalesLibre.descuento.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">IVA (13%)</Typography>
                      <Typography variant="h6">${totalesLibre.iva.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                      <Typography variant="h5" color="primary.main" fontWeight="bold">
                        ${totalesLibre.total.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            // Venta de Productos
            <Grid container spacing={isMobile ? 2 : 2}>
              {/* Fecha/Hora */}
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Fecha y Hora"
                  value={ventaProductos.fechaHora}
                  onChange={handleVentaProductosDateChange}
                  slotProps={{
                    textField: { 
                      fullWidth: true, 
                      size: isMobile ? 'medium' : 'medium' 
                    },
                  }}
                />
              </Grid>

              {/* Cliente */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={clientesList}
                  getOptionLabel={(option) => option.nombre || ''}
                  value={ventaProductos.cliente}
                  onChange={handleClienteProductosChange}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Cliente" 
                      size={isMobile ? 'medium' : 'medium'} 
                      placeholder="Seleccionar cliente" 
                    />
                  )}
                />
              </Grid>

              {/* Lista de productos */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Productos</Typography>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size={isMobile ? "medium" : "small"}
                    onClick={agregarProducto}
                    sx={isMobile ? { minHeight: 48 } : {}}
                  >
                    {isMobile ? "Agregar" : "Agregar Producto"}
                  </Button>
                </Box>
                
                {errors.productos && (
                  <Typography color="error" variant="caption" sx={{ mb: 1, display: 'block' }}>
                    {errors.productos}
                  </Typography>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell>Producto</TableCell>
                        <TableCell align="center" sx={{ width: 80 }}>Cant.</TableCell>
                        <TableCell align="center" sx={{ width: 100 }}>Precio</TableCell>
                        <TableCell align="center" sx={{ width: 60 }}>Desc</TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>Subt.</TableCell>
                        <TableCell align="center" sx={{ width: 50 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ventaProductos.productos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No hay productos agregados
                          </TableCell>
                        </TableRow>
                      ) : (
                        ventaProductos.productos.map((producto, index) => (
                          <TableRow key={producto.id}>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={productosList}
                                getOptionLabel={(option) => `${option.codigo} - ${option.nombre}`}
                                value={producto.producto}
                                onChange={(event, newValue) => handleProductoChange(index, 'producto', newValue)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Seleccionar"
                                    error={!!errors[`producto_${index}`]}
                                  />
                                )}
                                sx={{ minWidth: 150 }}
                              />
                              {errors[`producto_${index}`] && (
                                <Typography variant="caption" color="error">
                                  {errors[`producto_${index}`]}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={producto.cantidad}
                                onChange={(e) => handleProductoChange(index, 'cantidad', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                sx={{ width: 60 }}
                                error={!!errors[`cantidad_${index}`]}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={producto.precio}
                                onChange={(e) => handleProductoChange(index, 'precio', parseFloat(e.target.value) || 0)}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={producto.descuento}
                                onChange={(e) => handleProductoChange(index, 'descuento', parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                                sx={{ width: 50 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">
                                ${calcularSubtotalProducto(producto).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="medium"
                                color="error"
                                onClick={() => eliminarProducto(index)}
                                sx={{ 
                                  // Touch target grande
                                  minWidth: 48, 
                                  minHeight: 48,
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Descuento Total y Medio de Pago */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Descuento Total %"
                  type="number"
                  fullWidth
                  size={isMobile ? 'medium' : 'medium'}
                  value={ventaProductos.descuentoTotal}
                  onChange={handleDescuentoTotalChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Medio de Pago"
                  fullWidth
                  size={isMobile ? 'medium' : 'medium'}
                  value={ventaProductos.medioPago}
                  onChange={handleMedioPagoProductosChange}
                >
                  {medioPagoOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Totales */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                      <Typography variant="h6">${totalesProductos.subtotal.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Desc ({ventaProductos.descuentoTotal || 0}%)</Typography>
                      <Typography variant="h6" color="error.main">-${totalesProductos.descuentoTotal.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">IVA (13%)</Typography>
                      <Typography variant="h6">${totalesProductos.iva.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                      <Typography variant="h5" color="primary.main" fontWeight="bold">
                        ${totalesProductos.total.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions 
          sx={{ 
            px: 3, 
            py: 2, 
            borderTop: '1px solid #e0e0e0',
            // Footer fijo en móvil
            ...(isMobile && {
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
            }),
          }}
        >
          <Button 
            onClick={handleClose} 
            color="inherit" 
            variant="outlined"
            sx={actionButtonStyles}
          >
            Cancelar
          </Button>
          {tabValue === 0 ? (
            <Button 
              onClick={handleSaveVentaLibre} 
              variant="contained" 
              color="primary"
              sx={actionButtonStyles}
            >
              Guardar Venta
            </Button>
          ) : (
            <Button 
              onClick={handleSaveVentaProductos} 
              variant="contained" 
              color="primary"
              sx={actionButtonStyles}
            >
              Guardar Venta
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default NewSaleDialog;
